import { useMemo } from 'react';
import { When } from 'react-if';
import { DateTime } from 'luxon';
import { currencyFormatter, toDecimal } from 'shared';

import { classNames } from '../../../../../common/lib/classNames';
import { useContact } from '../../../../hooks/useContact';
import { useGetSelectedOrganisation } from '../../../../hooks/useGetSelectedOrganisation';
import { WrappedLink } from '../../../WrappedLink';
import { useContactAISummaryTexts } from '../ContactAISummary';

import { useContactOpenQuotes, useContactUnpaidInvoices } from './contactPageData';

export const cardClass = `shrink-0 rounded-xl border border-gray-200 bg-white`;
const linkClass = `text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline`;

/** Same day boundaries as the invoice aging board */
const buckets = [
  { label: `Current`, max: 0, color: `bg-gray-400` },
  { label: `1-30`, max: 30, color: `bg-orange-300` },
  { label: `31-60`, max: 60, color: `bg-orange-400` },
  { label: `61-90`, max: 90, color: `bg-red-400` },
  { label: `90+`, max: Infinity, color: `bg-red-700` },
];

/** Whole days past the due date in the org's timezone, negative when not yet due */
function daysOverdue(dueDate: string | undefined, timezone: string) {
  if (!dueDate) return 0;

  return Math.floor(DateTime.now().setZone(timezone).startOf(`day`)
    .diff(DateTime.fromISO(dueDate, { zone: timezone }).startOf(`day`), `days`).days);
}

export function ContactBalanceCard() {
  const { data: contact } = useContact();
  const organisation = useGetSelectedOrganisation();
  const { invoices, truncated } = useContactUnpaidInvoices();
  const timezone = organisation?.validatedTimezone;
  const format = currencyFormatter(organisation?.baseCurrency || `USD`);

  const ageing = useMemo(() => {
    const totals = buckets.map(bucket => ({ ...bucket, cents: 0 }));

    invoices.forEach(invoice => {
      const days = daysOverdue(invoice.dueDate, timezone);
      const bucket = totals.find(b => days <= b.max);

      // Home currency, same conversion as the contact's outstanding balance
      bucket.cents += (invoice.totals?.dueInCents || 0) * (invoice.exchangeRate || 1);
    });

    return totals;
  }, [invoices, timezone]);

  if (!contact) return null;

  const overdue = contact.overdueBalanceHome || 0;
  const ageingTotal = ageing.reduce((sum, b) => sum + Math.max(b.cents, 0), 0);

  return (
    <div className={ classNames(cardClass, `flex flex-col gap-2.5 px-4 py-3.5`) }>
      <div className={ `flex items-baseline justify-between gap-3` }>
        <span className={ `flex flex-col gap-0.5` }>
          <span className={ `text-xs text-gray-500` }>{ `Outstanding` }</span>
          <span className={ `text-2xl font-semibold tabular-nums text-gray-900` }>
            { format(toDecimal(contact.outstandingBalanceHome || 0)) }
          </span>
        </span>
        <When condition={ overdue > 0 }>
          <span className={ `text-right text-[0.8125rem] text-red-700` }>
            { `${format(toDecimal(overdue))} overdue` }
            <When condition={ !!contact.oldestDaysOverdue }>
              <br />
              { `oldest ${contact.oldestDaysOverdue} ${contact.oldestDaysOverdue === 1 ? `day` : `days`}` }
            </When>
          </span>
        </When>
      </div>

      <When condition={ ageingTotal > 0 && !truncated }>
        <div className={ `flex h-2 gap-0.5 overflow-hidden rounded` }>
          { ageing.filter(b => b.cents > 0).map(b => (
            <span
              key={ b.label }
              className={ b.color }
              style={ { width: `${(b.cents / ageingTotal) * 100}%` } }
            />
          )) }
        </div>
      </When>

      <div className={ classNames(`grid grid-cols-5 gap-1 text-[0.6875rem] tabular-nums`, truncated && `hidden`) }>
        { ageing.map(b => (
          <span
            key={ b.label }
            className={ `flex min-w-0 flex-col gap-px` }
          >
            <span className={ `flex items-center gap-1 text-gray-500` }>
              <span className={ classNames(`h-[0.4375rem] w-[0.4375rem] shrink-0 rounded-sm`, b.color) } />
              { b.label }
            </span>
            <span className={ `truncate text-xs text-gray-900` }>{ format(toDecimal(Math.round(b.cents))) }</span>
          </span>
        )) }
      </div>

      <div className={ `grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 border-t border-gray-100 pt-2 text-[0.8125rem] tabular-nums` }>
        <span className={ `text-gray-500` }>{ `Revenue, last 12 months` }</span>
        <span className={ `text-right` }>{ format(toDecimal(contact.totalRevenueHomeLast12Months || 0)) }</span>
        <span className={ `text-gray-500` }>{ `Unapplied credit` }</span>
        <span className={ `text-right` }>{ format(toDecimal(contact.unappliedCreditsTotalHome || 0)) }</span>
        <span className={ `text-gray-500` }>{ `Avg days to pay` }</span>
        <span className={ `text-right` }>{ contact.paidInvoiceCount ? contact.avgDaysToPay ?? 0 : `—` }</span>
      </div>
    </div>
  );
}

export function ContactOpenInvoicesCard() {
  const { data: contact } = useContact();
  const organisation = useGetSelectedOrganisation();
  const { invoices } = useContactUnpaidInvoices();
  const openQuotes = useContactOpenQuotes();
  const timezone = organisation?.validatedTimezone;

  if (!contact) return null;

  return (
    <div className={ classNames(cardClass, `flex flex-col`) }>
      <div className={ `flex items-center justify-between px-4 pb-1.5 pt-3` }>
        <span className={ `text-sm font-semibold text-gray-900` }>{ `Open invoices` }</span>
        <WrappedLink
          to={ `/contacts/${contact.id}/invoices` }
          className={ classNames(linkClass, `text-[0.8125rem]`) }
        >
          { `All →` }
        </WrappedLink>
      </div>

      <When condition={ invoices.length === 0 }>
        <p className={ `border-t border-gray-100 px-4 py-3 text-[0.8125rem] text-gray-500` }>{ `No unpaid invoices` }</p>
      </When>

      { invoices.map(invoice => {
        const days = daysOverdue(invoice.dueDate, timezone);
        const format = currencyFormatter(invoice.currency, organisation?.baseCurrency);

        return (
          <div
            key={ invoice.id }
            className={ `grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 border-t border-gray-100 px-4 py-2 text-[0.8125rem] tabular-nums` }
          >
            <WrappedLink
              to={ `/invoices/${invoice.id}` }
              className={ classNames(linkClass, `truncate font-medium`) }
            >
              { invoice.number || `Invoice` }
            </WrappedLink>
            <span className={ `text-right font-semibold text-gray-900` }>{ format(toDecimal(invoice.totals?.dueInCents || 0)) }</span>
            <span className={ `text-xs text-gray-500` }>
              { invoice.dueDate ? `Due ${DateTime.fromISO(invoice.dueDate).toFormat(`d LLL yyyy`)}` : `No due date` }
            </span>
            <span className={ classNames(`text-right text-xs`, days > 0 ? `text-red-700` : `text-gray-500`) }>
              { days > 0 ? `${days} ${days === 1 ? `day` : `days`} overdue`
                : days === 0 ? `Due today`
                  : `Due in ${-days} ${days === -1 ? `day` : `days`}` }
            </span>
          </div>
        );
      }) }

      { openQuotes.map(quote => (
        <div
          key={ quote.id }
          className={ `border-t border-gray-100 px-4 py-2 text-xs text-gray-500` }
        >
          { [
            `Quote ${quote.number}`,
            quote.totalAmountFormatted,
            quote.expiryDateString ? `expires ${DateTime.fromISO(quote.expiryDateString).toFormat(`d LLL yyyy`)}` : null,
          ].filter(Boolean).join(` · `) }
        </div>
      )) }
    </div>
  );
}

export function ContactAISummaryCard() {
  const texts = useContactAISummaryTexts();

  if (!texts.length) return null;

  return (
    <div className={ classNames(cardClass, `flex flex-col gap-1.5 px-4 py-3 text-[0.8125rem] leading-normal text-gray-700`) }>
      <span className={ `flex gap-1.5 text-xs text-gray-500` }>
        <span className={ `text-violet-600` }>{ `✦` }</span>
        { `AI summary` }
      </span>
      { texts.map((text, i) => (
        <span
          key={ i }
          className={ `text-pretty` }
        >
          { `• ${text.trim()}` }
        </span>
      )) }
    </div>
  );
}
